import { useContext, useState } from 'react';

import { useFlag } from '@unleash/proxy-client-react';
import { useIntl } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { NotificationContext, SpecialistContext } from 'context';
import { confirmedUserNonRisk } from 'services/user';
import { confirmedUserWithRisk } from 'services/user/user';

import Drawer from 'components/base/Drawer';

import DrawerForwardAlert from './ForwardAlert';
import ForwardAlertUnavailable from './ForwardAlertUnavailable';
import DrawerForwardAlertV2 from './ForwardAlertV2';
import ForwardUnavailableByGeoCode from './ForwardUnavailableByGeoCode';
import SelectCriticalityQualifiers from './SelectCriticalityQualifiers';

import {
  DrawerValidateCriticalityQualifiersProps,
  RadioOptValueType,
} from './types';

const DrawerValidateCriticalityQualifiers: React.FC<DrawerValidateCriticalityQualifiersProps> =
  ({ openDrawer, onCloseDrawer, cognitoId, hasExtUsers }) => {
    const intl = useIntl();
    const forwardAlertV2Enabled = useFlag(FeatureToggle.ForwardAlertV2);
    const { specialistInfo } = useContext(SpecialistContext);

    const { setNotification } = useContext(NotificationContext);

    const hasGeoPermission =
      typeof specialistInfo?.geoCode !== 'undefined' &&
      specialistInfo.geoCode === 'BR';

    const [note, setNote] = useState('');
    const [page, setPage] = useState(1);
    const [selectedValue, setSelectedValue] =
      useState<RadioOptValueType>('notCritical');

    const confirmedCriticalityWithRisk = async (jsonNote?: string) => {
      const forwardNote = jsonNote ?? note;
      if (!forwardNote && hasExtUsers && hasGeoPermission) {
        setNotification({
          message: intl.formatMessage({
            id: 'ENDUSER.DRAWER.CRITICAL.FORWARDALERT.TEXTAREA.ERROR',
          }),
          type: 'failure',
        });
        return;
      }

      try {
        await confirmedUserWithRisk(cognitoId, forwardNote);
        setNotification({
          message: intl.formatMessage({
            id: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.SUCCESS',
          }),
          type: 'success',
        });
        if (hasGeoPermission && hasExtUsers) {
          setPage(1);
          setNote('');
        } else {
          setPage(3);
        }
      } catch (error) {
        setNotification({
          message: intl.formatMessage({
            id: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.WARNING',
          }),
          type: 'failure',
        });
      } finally {
        if (hasGeoPermission && hasExtUsers) {
          onCloseDrawer();
        }
      }
    };

    const confirmedCriticalityNonCritical = async () => {
      if (selectedValue === 'isCritical') {
        if (!hasGeoPermission && !hasExtUsers) {
          await confirmedCriticalityWithRisk();
          return;
        }

        setPage(2);
        return;
      }

      try {
        await confirmedUserNonRisk(cognitoId);
        setNotification({
          message: intl.formatMessage({
            id: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.SUCCESS',
          }),
          type: 'success',
        });
        setPage(1);
        setNote('');
      } catch (error) {
        setNotification({
          message: intl.formatMessage({
            id: 'ENDUSER.DRAWER.CRITICAL.NOTIFICATION.WARNING',
          }),
          type: 'failure',
        });
      } finally {
        onCloseDrawer();
      }
    };

    const handleBackPage = () => {
      setPage(1);
    };

    const paginationComponents = {
      1: (
        <SelectCriticalityQualifiers
          setSelectedValue={setSelectedValue}
          selectedValue={selectedValue}
          confirmedCriticality={confirmedCriticalityNonCritical}
        />
      ),
      2: forwardAlertV2Enabled ? (
        <DrawerForwardAlertV2
          confirmedCriticality={confirmedCriticalityWithRisk}
        />
      ) : (
        <DrawerForwardAlert
          confirmedCriticality={confirmedCriticalityWithRisk}
          setNoteValue={setNote}
          note={note}
        />
      ),
      3: hasGeoPermission ? (
        <ForwardAlertUnavailable
          hasExtProfessionals={hasExtUsers}
          confirmedCriticality={confirmedCriticalityWithRisk}
        />
      ) : (
        <ForwardUnavailableByGeoCode
          confirmedCriticality={() => onCloseDrawer()}
        />
      ),
    };

    const renderForwardAlert = () => {
      if (page === 1) {
        return paginationComponents[1];
      }

      if (hasExtUsers) {
        return paginationComponents[2];
      }

      return paginationComponents[3];
    };

    return (
      <Drawer
        openDrawer={openDrawer}
        onCloseDrawer={onCloseDrawer}
        onSetPage={handleBackPage}
        showArrowLeftIcon={page > 1}
      >
        {renderForwardAlert()}
      </Drawer>
    );
  };

export default DrawerValidateCriticalityQualifiers;
